import {useCallback, useEffect, useRef, useState} from 'react'
import {PREVIEW_MIME_TYPES, STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import getFileMIMEtype from 'src/components-new/feedback/getFileMIMEtype'

interface ViewerState {
  open: boolean
  filePath: string
  mime: string
  fileName: string
  status: string
}

interface PreviewParams {
  fileId: string
  fileName: string
  downloadName: string
}

interface UseFilePreviewReturn {
  viewerState: ViewerState
  statusDownload: string | undefined
  preview: (params: PreviewParams) => Promise<void>
  close: () => void
}

const INITIAL_VIEWER_STATE: ViewerState = {
  open: false,
  filePath: '',
  mime: '',
  fileName: '',
  status: STATUS.PENDING
}

export default function useFilePreview(): UseFilePreviewReturn {
  const [viewerState, setViewerState] = useState<ViewerState>(INITIAL_VIEWER_STATE)
  const [statusDownload, setStatusDownload] = useState<string | undefined>(undefined)
  const objectUrlRef = useRef<string | null>(null)
  const previewIdRef = useRef<number>(0)

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
  }, [])

  const close = useCallback(() => {
    previewIdRef.current += 1
    setViewerState(INITIAL_VIEWER_STATE)
    revokeObjectUrl()
  }, [revokeObjectUrl])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) {
      close()
    }
  }, [statusDownload, close])

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [])

  const downloadFile = useCallback(async (params: PreviewParams) => {
    const {data: fileResponse} = await apiClient.download(params.fileId, setStatusDownload)
    const blob = new Blob([new Uint8Array(fileResponse)], {type: ''})
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = params.downloadName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }, [])

  const preview = useCallback(async (params: PreviewParams) => {
    const mime = getFileMIMEtype(params.fileName)

    if (mime !== undefined && PREVIEW_MIME_TYPES.includes(mime)) {
      revokeObjectUrl()
      const id = ++previewIdRef.current
      setViewerState({
        open: true,
        filePath: '',
        mime,
        fileName: params.downloadName,
        status: STATUS.PENDING
      })
      const {data: fileResponse} = await apiClient.download(params.fileId, setStatusDownload)
      if (id !== previewIdRef.current) return
      const blob = new Blob([new Uint8Array(fileResponse)], {type: mime})
      const url = (window.URL || window.webkitURL).createObjectURL(blob)
      objectUrlRef.current = url
      setViewerState(prev => ({...prev, filePath: url, status: STATUS.FULFILLED}))
    } else {
      await downloadFile(params)
    }
  }, [revokeObjectUrl, downloadFile])

  return {viewerState, statusDownload, preview, close}
}
